/**
 * NMFC density-based freight classification (13-tier table effective 2025-07-19).
 * Authoritative frontend source — keep in sync with
 * drumkit/common/helpers/ltl/freight_class_density.go (and verona/vulcan copies).
 *
 * @see https://www.freightquote.com/how-to-ship-freight/freight-class-density-calculator/
 */
export const DENSITY_CLASS_BREAKPOINTS: Array<{
  minPcf: number;
  freightClass: string;
}> = [
  { minPcf: 50, freightClass: '50' },
  { minPcf: 35, freightClass: '55' },
  { minPcf: 30, freightClass: '60' },
  { minPcf: 22.5, freightClass: '65' },
  { minPcf: 15, freightClass: '70' },
  { minPcf: 12, freightClass: '85' },
  { minPcf: 10, freightClass: '92.5' },
  { minPcf: 8, freightClass: '100' },
  { minPcf: 6, freightClass: '125' },
  { minPcf: 4, freightClass: '175' },
  { minPcf: 2, freightClass: '250' },
  { minPcf: 1, freightClass: '300' },
];

/** Sub-1 PCF tier from densityToFreightClass fallback; not a breakpoint min. */
export const NMFC_DENSITY_FALLBACK_FREIGHT_CLASS = '400' as const;

/** All selectable freight classes from the 2025 NMFC density table (13 tiers). */
export const NMFC_DENSITY_FREIGHT_CLASSES: readonly string[] = [
  ...new Set([
    ...DENSITY_CLASS_BREAKPOINTS.map(({ freightClass }) => freightClass),
    NMFC_DENSITY_FALLBACK_FREIGHT_CLASS,
  ]),
].sort((a, b) => Number(a) - Number(b));

const NMFC_DENSITY_FREIGHT_CLASS_SET = new Set(NMFC_DENSITY_FREIGHT_CLASSES);

function canonicalizeFreightClassToken(value: string): string {
  const trimmedValue = value.trim();
  if (!/^\d+(?:\.\d+)?$/.test(trimmedValue)) {
    return trimmedValue;
  }

  return String(Number(trimmedValue));
}

export function normalizeLtlFreightClassSuggestion(value?: string): string {
  if (!value) {
    return '';
  }

  const trimmedValue = value.trim();
  const exactValue = canonicalizeFreightClassToken(trimmedValue);
  if (NMFC_DENSITY_FREIGHT_CLASS_SET.has(exactValue)) {
    return exactValue;
  }

  const numericTokens =
    trimmedValue
      .match(/\b\d+(?:\.\d+)?\b/g)
      ?.map(canonicalizeFreightClassToken)
      .filter((token) => NMFC_DENSITY_FREIGHT_CLASS_SET.has(token)) ?? [];
  const uniqueTokens = [...new Set(numericTokens)];

  if (uniqueTokens.length === 1) {
    return uniqueTokens[0];
  }

  return '';
}

export function nmfcDensityFreightClassSelectOptions(): Array<{
  value: string;
  label: string;
}> {
  return NMFC_DENSITY_FREIGHT_CLASSES.map((freightClass) => ({
    value: freightClass,
    label: freightClass,
  }));
}

export function densityToFreightClass(pcf: number): string {
  for (const { minPcf, freightClass } of DENSITY_CLASS_BREAKPOINTS) {
    if (pcf >= minPcf) {
      return freightClass;
    }
  }

  return NMFC_DENSITY_FALLBACK_FREIGHT_CLASS;
}
